import { useEffect, useState, useRef } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { galleryApi, userApi, usageApi } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import PatternViewer from '../components/PatternViewer'
import ShareCodeModal from '../components/ShareCodeModal'
import { findBrandByName, getDefaultBeadSize } from '../utils/beadData'
import { exportPatternPng, exportPatternPdf, downloadDataUrl, downloadBlob } from '../utils/exportPattern'

export default function GalleryDetail() {
  const { id, code: codeParam } = useParams()
  const [searchParams] = useSearchParams()
  const shareCode = codeParam || searchParams.get('code') || ''
  const navigate = useNavigate()
  const { user, refreshUsage } = useAuth()
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showGrid, setShowGrid] = useState(true)
  const [showGroupLines, setShowGroupLines] = useState(true)
  const [showColorCodes, setShowColorCodes] = useState(true)
  const [showShareModal, setShowShareModal] = useState(false)
  const patternCanvasRef = useRef(null)

  useEffect(() => {
    const fetchItem = async () => {
      try {
        setLoading(true)
        let data
        if (shareCode) {
          const res = await galleryApi.getByShareCode(shareCode)
          data = res.data
        } else {
          const res = await galleryApi.get(id)
          data = res.data
        }
        setItem(data)
      } catch (err) {
        setError(err.response?.data?.error || err.message || '加载失败')
      } finally {
        setLoading(false)
      }
    }
    fetchItem()
  }, [id, shareCode])

  const handleLike = async () => {
    if (!user) {
      navigate('/login')
      return
    }
    try {
      const { data } = await galleryApi.like(id)
      setItem((prev) =>
        prev
          ? {
              ...prev,
              isLiked: data.liked,
              likeCount: data.liked ? prev.likeCount + 1 : Math.max(0, prev.likeCount - 1),
            }
          : prev
      )
    } catch (err) {
      alert(err.response?.data?.error || '点赞失败')
    }
  }

  const handleFollow = async () => {
    if (!user) {
      navigate('/login')
      return
    }
    try {
      const { data } = await userApi.follow(item.author.id)
      setItem((prev) => (prev ? { ...prev, isFollowing: data.following } : prev))
    } catch (err) {
      alert(err.response?.data?.error || '关注失败')
    }
  }

  const exportPng = async () => {
    if (!item?.pattern) return
    const hd = !!user?.isPremium
    try {
      await usageApi.export({ type: 'png', hd, width: item.pattern.width, height: item.pattern.height })
    } catch (err) {
      alert(err.response?.data?.error || '导出次数已用完，请升级会员~')
      return
    }

    const dataUrl = await exportPatternPng({
      pattern: item.pattern,
      brand,
      user,
      showGrid,
      showGroupLines,
      showColorCodes,
      beadSize: getDefaultBeadSize(brand?.id),
      hd,
      // 署名原作者，而非导出者
      authorName: item.author?.username || item.author?.email || null,
      authorId: item.author?.id || null,
    })
    await downloadDataUrl(dataUrl, `doutian_${item.id}_${item.pattern.width}x${item.pattern.height}.png`)
    refreshUsage()
  }

  const exportPdf = async () => {
    if (!item?.pattern) return
    const hd = !!user?.isPremium
    try {
      await usageApi.export({ type: 'pdf', hd, width: item.pattern.width, height: item.pattern.height })
    } catch (err) {
      alert(err.response?.data?.error || '导出次数已用完，请升级会员~')
      return
    }

    const pdf = await exportPatternPdf({
      pattern: item.pattern,
      brand,
      user,
      showGrid,
      showGroupLines,
      showColorCodes,
      maxWidth: 800,
      beadSize: getDefaultBeadSize(brand?.id),
      hd,
      // 署名原作者，而非导出者
      authorName: item.author?.username || item.author?.email || null,
      authorId: item.author?.id || null,
    })
    downloadBlob(pdf.output('blob'), `doutian_${item.id}_${item.pattern.width}x${item.pattern.height}.pdf`)
    refreshUsage()
  }

  const isAuthor = user && item && user.id === item.author?.id

  const handleCreateShareCode = async () => {
    if (!item) return
    try {
      const { data } = await galleryApi.createShareCode(item.id)
      setItem((prev) => (prev ? { ...prev, shareCode: data.shareCode } : prev))
      setShowShareModal(true)
    } catch (err) {
      alert(err.response?.data?.error || '生成分享码失败')
    }
  }

  const handleDelete = async () => {
    if (!item) return
    if (!window.confirm('确定要删除这张图纸吗？删除后无法恢复。')) return
    try {
      await galleryApi.delete(item.id)
      navigate('/profile')
    } catch (err) {
      alert(err.response?.data?.error || '删除失败')
    }
  }

  if (loading) return <div className="py-20 text-center text-cocoa-light">加载中…</div>
  if (error || !item) {
    return (
      <div className="mx-auto max-w-7xl py-20 text-center text-cocoa-light">
        {error || '作品不存在'}
      </div>
    )
  }

  const brand = findBrandByName(item.brand)

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="card space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to={`/user/${item.author.id}`}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-blush text-lg font-bold text-white"
            >
              {(item.author.username || item.author.email || '?')[0]}
            </Link>
            <div>
              <Link to={`/user/${item.author.id}`} className="font-semibold text-cocoa hover:underline">
                {item.author.username || item.author.email}
              </Link>
              {item.title && <p className="text-sm font-medium text-cocoa">{item.title}</p>}
              <p className="text-xs text-cocoa-light">
                {item.brand} · {item.width}×{item.height} · {item.total} 豆
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isAuthor && (
              <button
                onClick={handleFollow}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                  item.isFollowing
                    ? 'border border-lavender/60 text-cocoa hover:bg-lavender/30'
                    : 'bg-blush text-white shadow-soft hover:bg-blush-dark'
                }`}
              >
                {item.isFollowing ? '已关注' : '+ 关注'}
              </button>
            )}
            <button
              onClick={handleLike}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
                item.isLiked
                  ? 'bg-blush text-white shadow-soft'
                  : 'border border-lavender/60 text-cocoa hover:bg-lavender/30'
              }`}
            >
              ♥ {item.likeCount}
            </button>
            {isAuthor && (
              <>
                <button
                  onClick={handleCreateShareCode}
                  className="rounded-full bg-mint px-4 py-1.5 text-sm font-semibold text-cocoa shadow-soft transition hover:bg-mint-dark"
                >
                  分享
                </button>
                <button
                  onClick={handleDelete}
                  className="rounded-full border border-red-300 bg-red-50 px-4 py-1.5 text-sm font-semibold text-red-600 transition hover:bg-red-100"
                >
                  删除
                </button>
              </>
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-lavender/30 bg-white">
          <img src={item.imageUrl} alt="gallery" className="mx-auto max-h-[60vh] w-auto object-contain" />
        </div>

        {/* 作品许可协议 */}
        <p className="text-xs text-cocoa-light">
          本作品采用{' '}
          <a
            href="https://creativecommons.org/licenses/by-nc-nd/4.0/deed.zh-hans"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-blush-dark underline"
          >
            CC BY-NC-ND 4.0
          </a>{' '}
          协议共享：署名 · 非商业性使用 · 禁止演绎
        </p>
      </section>

      {item.canViewPattern && item.pattern ? (
        <section className="card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-bold text-cocoa">🧵 拼豆图纸</h3>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-cocoa-light">
                <input
                  type="checkbox"
                  checked={showGrid}
                  onChange={(e) => setShowGrid(e.target.checked)}
                  className="h-3.5 w-3.5 accent-blush"
                />
                网格
              </label>
              <label className="flex items-center gap-1 text-xs text-cocoa-light">
                <input
                  type="checkbox"
                  checked={showGroupLines}
                  onChange={(e) => setShowGroupLines(e.target.checked)}
                  className="h-3.5 w-3.5 accent-blush"
                />
                辅助线
              </label>
              <label className="flex items-center gap-1 text-xs text-cocoa-light">
                <input
                  type="checkbox"
                  checked={showColorCodes}
                  onChange={(e) => setShowColorCodes(e.target.checked)}
                  className="h-3.5 w-3.5 accent-blush"
                />
                色号
              </label>
              <button
                onClick={() => navigate(shareCode ? `/bead-mode/share/${shareCode}` : `/bead-mode/${item.id}`)}
                className="rounded-lg border border-mint/60 bg-mint/20 px-3 py-1.5 text-xs font-semibold text-cocoa transition hover:bg-mint/40"
                title="逐色号高亮辅助拼图（永久会员）"
              >
                🧩 拼豆模式
              </button>
              <button onClick={exportPng} className="btn-soft text-sm">导出 PNG 🖼️</button>
              <button onClick={exportPdf} className="btn-primary text-sm">导出 PDF 📄</button>
            </div>
          </div>
          <PatternViewer pattern={item.pattern} brand={brand} showGrid={showGrid} showColorCodes={showColorCodes} canvasRef={patternCanvasRef} />
        </section>
      ) : (
        <section className="card flex flex-col items-center justify-center gap-4 py-12 text-center">
          <div className="text-5xl">🔒</div>
          <div>
            <p className="text-cocoa font-medium">图纸已被锁定</p>
            <p className="text-sm text-cocoa-light">
              {user
                ? '关注作者或升级会员即可查看完整图纸'
                : '登录后关注作者或升级会员查看完整图纸'}
            </p>
          </div>
          {!user && (
            <Link to="/login" className="btn-primary">
              去登录
            </Link>
          )}
        </section>
      )}

      {showShareModal && item?.shareCode && (
        <ShareCodeModal code={item.shareCode} onClose={() => setShowShareModal(false)} />
      )}
    </div>
  )
}
